import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminEditRequestInput } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useToasts } from '../lib/useToasts';
import { HistoryTimeline, RiskPill, StageTracker, StatusBadge, ToolLogo, riskFor } from '../components/atoms';
import { CompanyBackground } from '../components/CompanyBackground';
import { formatDate, formatDateTime } from '../lib/format';
import type { Category, FeatureFlagKey, RequestRecord, Role, Status, StatusEvent } from '@air/shared';
import { CATEGORIES, STATUS_LABELS } from '@air/shared';

type ActionKind = 'approve' | 'conditional' | 'clarify' | 'reject';

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, realRole, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const toasts = useToasts();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  // Feature flags determine whether non-admin roles see the CompanyBackground
  // card. Admin always sees it. Flags are queried separately (cheap, cached).
  const flags = useQuery({
    queryKey: ['feature-flags'],
    queryFn: api.featureFlags,
    staleTime: 60_000,
  });

  const detail = useQuery({
    queryKey: ['request', id],
    queryFn: () => api.getRequest(id!),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: async (input: { kind: ActionKind; comment: string; conditions: string; role: 'it' | 'legal' }) => {
      const { kind, comment, conditions, role } = input;
      const body: { comment?: string; conditions?: string } = {};
      if (comment) body.comment = comment;
      if (kind === 'conditional') body.conditions = conditions;
      if (role === 'it') {
        const action = kind === 'approve' ? 'approve' : kind === 'reject' ? 'reject' : 'request-clarification';
        return api.itAction(id!, action, body);
      }
      const action =
        kind === 'approve'
          ? 'approve'
          : kind === 'conditional'
            ? 'approve-with-conditions'
            : kind === 'reject'
              ? 'reject'
              : 'request-clarification';
      return api.legalAction(id!, action, body);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['request', id] });
      qc.invalidateQueries({ queryKey: ['queue-count'] });
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      toasts.push(actionToast(variables.kind, variables.role));
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Action failed.', 'err');
    },
  });

  const respond = useMutation({
    mutationFn: async (comment: string) => api.respond(id!, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['request', id] });
      toasts.push('Response sent.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Response failed.', 'err');
    },
  });

  const editMutation = useMutation({
    mutationFn: async (patch: AdminEditRequestInput) => api.adminEditRequest(id!, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['request', id] });
      qc.invalidateQueries({ queryKey: ['registry'] });
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      toasts.push('Request updated.');
      setEditing(false);
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Edit failed.', 'err');
    },
  });

  const deleteRequest = useMutation({
    mutationFn: async () => api.deleteRequest(id!),
    onSuccess: () => {
      toasts.push('Request deleted.', 'warn');
      qc.invalidateQueries({ queryKey: ['registry'] });
      qc.invalidateQueries({ queryKey: ['registry-counts'] });
      qc.invalidateQueries({ queryKey: ['queue-count'] });
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      navigate('/registry', { replace: true });
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Delete failed.', 'err');
      setPendingDelete(false);
    },
  });

  if (detail.isLoading) return <p className="muted">Loading…</p>;
  if (detail.error) return <p className="field__error">Could not load the request.</p>;
  if (!detail.data) return null;

  const { request: r, events } = detail.data;
  const isSubmitter = user?.id === r.submitterId;
  const isInClarification =
    r.status === 'it_clarification_requested' || r.status === 'legal_clarification_requested';

  const risk = riskFor({
    usesConfidentialData: r.usesConfidentialData,
    usesPrivilegedData: r.usesPrivilegedData,
    usesCustomerData: r.usesCustomerData,
  });

  const role = effectiveRole ?? 'employee';
  const showIntel = (() => {
    if (!r.vendor) return false;
    if (realRole === 'admin') return true;
    const flagKey: FeatureFlagKey | null =
      role === 'employee'
        ? 'vendor_intel_visible_to_employee'
        : role === 'it_reviewer'
          ? 'vendor_intel_visible_to_it_reviewer'
          : role === 'legal_reviewer'
            ? 'vendor_intel_visible_to_legal_reviewer'
            : null;
    if (!flagKey) return false;
    return !!flags.data?.[flagKey];
  })();

  return (
    <>
      {/* Top action row — Back, Print, admin Edit/Delete */}
      <div
        className="page-headbar reveal"
        style={{ justifyContent: 'space-between', marginBottom: 14 }}
      >
        <button className="btn btn--ghost btn--sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--secondary btn--sm" onClick={() => window.print()}>
            🖨 Print
          </button>
          {realRole === 'admin' && !editing && (
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setEditing(true)}
              title="Edit submitted fields (admin only)"
            >
              Edit
            </button>
          )}
          {realRole === 'admin' && (
            <button
              className="btn btn--danger btn--sm"
              onClick={() => setPendingDelete(true)}
              title="Permanently delete this request (admin only)"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {pendingDelete && (
        <DeleteConfirm
          ticket={r.ticketNumber}
          tool={r.softwareName}
          pending={deleteRequest.isPending}
          onCancel={() => setPendingDelete(false)}
          onConfirm={() => deleteRequest.mutate()}
        />
      )}

      {/* Header card: tool identity + tracker, all in one bordered card */}
      <div className="card reveal reveal-1" style={{ marginBottom: 22 }}>
        <div
          style={{
            padding: '22px 26px',
            display: 'flex',
            gap: 22,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <ToolLogo name={r.softwareName} logoUrl={r.logoUrl} size={56} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="page-overline mono" style={{ marginBottom: 6 }}>
              {r.ticketNumber}
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--color-navy)', margin: '0 0 6px', letterSpacing: '-.01em' }}>
              {r.softwareName}
            </h1>
            <div style={{ fontSize: 13, color: 'var(--color-steel)' }}>
              {r.vendor && (
                <>
                  <span>{r.vendor}</span> <span>·</span>{' '}
                </>
              )}
              <span>{r.category}</span> <span>·</span>{' '}
              <a
                href={r.websiteUrl}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--color-blue, #08489B)', textDecoration: 'none' }}
              >
                {r.websiteUrl} ↗
              </a>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <StatusBadge status={r.status} />
            <RiskPill risk={risk} />
            <div style={{ fontSize: 11, color: 'var(--color-steel)' }}>
              Submitted {formatDate(r.createdAt)} by <strong>{r.submitterName}</strong>
            </div>
          </div>
        </div>
        <StageTracker status={r.status} events={events} submitterName={r.submitterName} />
      </div>

      {/* 2-col body: LEFT = response/intel/conditions/reviewer/activity; RIGHT = details + reviewers card */}
      <div className="row--2col reveal reveal-2">
        <div className="stack">
          {isSubmitter && isInClarification && (
            <ClarificationResponder
              status={r.status}
              submitting={respond.isPending}
              onSubmit={(text) => respond.mutate(text)}
            />
          )}

          {showIntel && r.vendor && (
            <CompanyBackground
              vendor={r.vendor}
              vendorWebsite={r.websiteUrl}
              role={role}
            />
          )}

          {r.conditions && <ConditionsCard conditions={r.conditions} />}

          {effectiveRole && canReview(effectiveRole, r.status) && (
            <ReviewerActionPanel
              status={r.status}
              role={effectiveRole}
              submitting={mutation.isPending}
              onAction={(kind, comment, conditions) => {
                const reviewerRole =
                  r.status === 'pending_it_review' || r.status === 'it_clarification_requested'
                    ? 'it'
                    : 'legal';
                mutation.mutate({ kind, comment, conditions, role: reviewerRole });
              }}
            />
          )}

          <div className="card card--padded">
            <h3 className="card__title" style={{ marginBottom: 14 }}>
              Activity timeline
            </h3>
            <HistoryTimeline events={events} />
          </div>
        </div>

        <div className="stack">
          <div className="card card--padded">
            <h3 className="card__title" style={{ marginBottom: 14 }}>Request details</h3>
            {editing && realRole === 'admin' ? (
              <AdminEditPanel
                request={r}
                submitting={editMutation.isPending}
                onCancel={() => setEditing(false)}
                onSave={(patch) => editMutation.mutate(patch)}
              />
            ) : (
              <>
                {r.vendor && <DetailRow label="Vendor" value={r.vendor} />}
                <DetailRow label="Category" value={r.category} />
                <DetailRow label="Intended use" value={r.intendedUse} />
                <DetailRow label="Inputs" value={r.dataInputs} />
                <DetailRow label="Outputs" value={r.expectedOutputs} />
                <DetailRow label="Output usage" value={r.outputUsage} />
                <DetailRow label="Confidential data" value={r.usesConfidentialData ? 'Yes' : 'No'} />
                <DetailRow label="Privileged data" value={r.usesPrivilegedData ? 'Yes' : 'No'} />
                <DetailRow label="Customer data" value={r.usesCustomerData ? 'Yes' : 'No'} />
                {r.additionalNotes && <DetailRow label="Notes" value={r.additionalNotes} />}
              </>
            )}
          </div>

          <ReviewersCard status={r.status} events={events} />
        </div>
      </div>
    </>
  );
}

function ConditionsCard({ conditions }: Readonly<{ conditions: string }>) {
  const bullets = conditions
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="card card--padded">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 className="card__title" style={{ margin: 0 }}>Approval conditions</h3>
        <StatusBadge status="approved_with_conditions" />
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-steel)', margin: '0 0 12px', lineHeight: 1.6 }}>
        Legal approved this tool with the following conditions of use. By using the tool you attest
        to compliance.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--color-navy)', lineHeight: 1.7 }}>
        {bullets.length === 0 ? (
          <li>{conditions}</li>
        ) : (
          bullets.map((b) => <li key={b}>{b}</li>)
        )}
      </ul>
    </div>
  );
}

function ReviewersCard({
  status,
  events,
}: Readonly<{ status: Status; events: StatusEvent[] }>) {
  // We don't track per-request reviewer assignment yet. Surface the queue
  // state for each stage so the submitter can see who's looking. When the
  // queue actually has a named claimant in a future phase, swap this out
  // for the assigned user. For now: derive state from events when present.
  const itDone = events.find((e) => e.eventType === 'it_approved' || e.eventType === 'it_rejected');
  const legalDone = events.find(
    (e) =>
      e.eventType === 'legal_approved' ||
      e.eventType === 'legal_approved_with_conditions' ||
      e.eventType === 'legal_rejected',
  );

  function itState(): { label: string; muted: boolean } {
    if (status === 'pending_it_review') return { label: 'Reviewing now', muted: false };
    if (status === 'it_clarification_requested') return { label: 'Awaiting your reply', muted: false };
    if (itDone) return { label: 'Done', muted: true };
    return { label: 'Not yet', muted: true };
  }
  function legalState(): { label: string; muted: boolean } {
    if (status === 'pending_legal_review') return { label: 'Reviewing now', muted: false };
    if (status === 'legal_clarification_requested') return { label: 'Awaiting your reply', muted: false };
    if (legalDone) return { label: 'Done', muted: true };
    return { label: 'Not yet', muted: true };
  }

  return (
    <div className="card card--padded">
      <h3 className="card__title" style={{ marginBottom: 14 }}>Reviewers</h3>
      <ReviewerRow label="IT Review queue" state={itState()} />
      <div style={{ height: 12 }} />
      <ReviewerRow label="Legal Review queue" state={legalState()} />
    </div>
  );
}

function ReviewerRow({
  label,
  state,
}: Readonly<{ label: string; state: { label: string; muted: boolean } }>) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span
        className="comment__avatar"
        style={{ width: 28, height: 28, fontSize: 11 }}
        aria-hidden="true"
      >
        ⎯
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--color-navy)' }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--color-steel)' }}>Stage reviewer pool</div>
      </div>
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: state.muted ? 'var(--color-steel)' : 'var(--color-navy)',
        }}
      >
        {state.label}
      </span>
    </div>
  );
}

function DetailRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="review-section__row">
      <div className="review-section__label">{label}</div>
      <div className="review-section__value">{value}</div>
    </div>
  );
}

function canReview(role: Role | undefined, status: Status): boolean {
  if (!role) return false;
  if (role === 'admin') return status === 'pending_it_review' || status === 'pending_legal_review';
  if (role === 'it_reviewer') return status === 'pending_it_review';
  if (role === 'legal_reviewer') return status === 'pending_legal_review';
  return false;
}

function actionToast(kind: ActionKind, role: 'it' | 'legal'): string {
  if (kind === 'approve') return role === 'it' ? 'Approved — routed to Legal.' : 'Approved.';
  if (kind === 'conditional') return 'Approved with conditions.';
  if (kind === 'reject') return 'Rejected.';
  return 'Clarification requested.';
}

// Per-kind copy + button styling. Mirrors the design's detail.jsx panel.
const KIND_PLACEHOLDER_IT: Record<ActionKind, string> = {
  approve: "Optional notes — the request moves to Legal. They'll see your comment.",
  conditional: 'List the conditions of use. The requester sees these on the approval and is expected to comply.',
  clarify: "What do you need from the requester? They'll get an email and the request pauses until they respond.",
  reject: 'Why is this rejected? Required — visible to the requester. Be specific so they can act.',
};
const KIND_PLACEHOLDER_LEGAL: Record<ActionKind, string> = {
  approve: "Optional notes for the requester. Final approval — they're cleared to use the tool.",
  conditional: 'List the conditions of use. The requester sees these on the approval and is expected to comply.',
  clarify: "What do you need from the requester? They'll get an email and the request pauses until they respond.",
  reject: 'Why is this rejected? Required — visible to the requester. Be specific so they can act.',
};
const SUBMIT_BTN_CLASS: Record<ActionKind, string> = {
  approve: 'btn btn--success',
  conditional: 'btn btn--secondary',
  clarify: 'btn btn--warning',
  reject: 'btn btn--danger',
};

function submitLabel(kind: ActionKind, isLegal: boolean): string {
  if (kind === 'approve') return isLegal ? 'Mark Approved' : 'Approve & Route to Legal';
  if (kind === 'conditional') return 'Approve with Conditions';
  if (kind === 'clarify') return 'Send Clarification Request';
  return 'Reject Request';
}

function ReviewerActionPanel({
  status,
  role,
  submitting,
  onAction,
}: Readonly<{
  status: Status;
  role: Role;
  submitting: boolean;
  onAction: (kind: ActionKind, comment: string, conditions: string) => void;
}>) {
  const isLegal = status === 'pending_legal_review' || role === 'legal_reviewer';
  // No kind preselected — design shows tabs only until the user picks one.
  const [kind, setKind] = useState<ActionKind | null>(null);
  const [comment, setComment] = useState('');
  const [conditions, setConditions] = useState('');

  const buttons: Array<{ kind: ActionKind; label: string; sub: string }> = [
    { kind: 'approve', label: isLegal ? 'Approve' : 'Approve & Route', sub: isLegal ? 'Sign off' : 'Pass to Legal' },
  ];
  if (isLegal) buttons.push({ kind: 'conditional', label: 'With Conditions', sub: 'Approve w/ rules' });
  buttons.push({ kind: 'clarify', label: 'Ask Clarification', sub: 'Need more info' });
  buttons.push({ kind: 'reject', label: 'Reject', sub: 'Block this' });

  // Every reviewer outcome — including Approve — requires a comment (>=8 chars
  // per design) so the audit trail captures the rationale. Conditional
  // approval additionally requires conditions text.
  const needsConditions = kind === 'conditional';
  const submitDisabled =
    submitting ||
    kind === null ||
    comment.trim().length < 8 ||
    (needsConditions && conditions.trim().length === 0);

  const placeholders = isLegal ? KIND_PLACEHOLDER_LEGAL : KIND_PLACEHOLDER_IT;

  function cancel(): void {
    setKind(null);
    setComment('');
    setConditions('');
  }

  return (
    <div
      className="action-panel reveal reveal-1"
      style={{ marginBottom: 22, borderTop: `3px solid ${isLegal ? 'var(--color-teal, #007D61)' : 'var(--color-navy)'}` }}
    >
      <h3 className="action-panel__title">
        {isLegal ? 'Legal Review — your decision' : 'IT Review — your decision'}
      </h3>
      <p className="action-panel__sub">
        You're the current reviewer. Pick an outcome below; comments go to the requester &amp; full
        timeline.
      </p>
      <div className="action-tabs">
        {buttons.map((b) => (
          <button
            key={b.kind}
            data-kind={b.kind}
            className={`action-tab ${kind === b.kind ? 'is-active' : ''}`}
            onClick={() => setKind(b.kind)}
          >
            <span className="action-tab__icon">
              {b.kind === 'approve' ? '✓' : b.kind === 'conditional' ? '!' : b.kind === 'clarify' ? '?' : '×'}
            </span>
            <span>{b.label}</span>
          </button>
        ))}
      </div>
      {kind !== null && (
        <div style={{ marginTop: 4 }}>
          {needsConditions && (
            <div className="field" style={{ marginBottom: 12 }}>
              <label className="field__label">
                Conditions of use <span className="field__req">*</span>
              </label>
              <p className="field__help">
                One per line. These display on the approved record and the requester sees them on
                the timeline.
              </p>
              <textarea
                rows={3}
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                placeholder={'No member PHI or PII in prompts\nOutputs human-reviewed before publish\nRe-attestation required annually'}
              />
            </div>
          )}
          <div className="field">
            <label className="field__label">
              Comment <span className="field__req">*</span>
              <span style={{ fontSize: 11, color: 'var(--color-steel)', fontWeight: 500, marginLeft: 6 }}>
                visible to the requester
              </span>
            </label>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={placeholders[kind]}
            />
            {comment.trim().length < 8 && (
              <p className="field__help" style={{ color: 'var(--color-steel)' }}>
                At least 8 characters — every reviewer action requires a substantive comment so the
                audit trail captures your reasoning.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <button className="btn btn--ghost btn--sm" onClick={cancel} disabled={submitting}>
              Cancel
            </button>
            <button
              className={SUBMIT_BTN_CLASS[kind]}
              disabled={submitDisabled}
              onClick={() => onAction(kind, comment, conditions)}
            >
              {submitting ? 'Working…' : submitLabel(kind, isLegal)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteConfirm({
  ticket,
  tool,
  pending,
  onCancel,
  onConfirm,
}: Readonly<{
  ticket: string;
  tool: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>) {
  const [typed, setTyped] = useState('');
  const phrase = ticket;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,26,114,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onCancel}
    >
      <div
        className="card card--padded"
        style={{ maxWidth: 480, width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="card__title" style={{ marginBottom: 8 }}>
          Permanently delete this request?
        </h3>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--color-navy)', margin: '0 0 14px' }}>
          You're about to hard-delete <strong>{tool}</strong> ({ticket}) and its entire audit trail
          (status events + queued notifications). This cannot be undone. If you only want to close
          the request, use Reject from the reviewer panel instead — that keeps the history.
        </p>
        <div className="field">
          <label className="field__label">
            To confirm, type the ticket number <span className="mono">{phrase}</span>
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={phrase}
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn btn--danger"
            onClick={onConfirm}
            disabled={pending || typed.trim() !== phrase}
          >
            {pending ? 'Deleting…' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClarificationResponder({
  status,
  submitting,
  onSubmit,
}: Readonly<{ status: Status; submitting: boolean; onSubmit: (text: string) => void }>) {
  const [text, setText] = useState('');
  return (
    <div className="alert alert--warn reveal reveal-1" style={{ marginBottom: 22, display: 'block' }}>
      <strong>Reviewer needs more info ({STATUS_LABELS[status]}).</strong>
      <p style={{ marginTop: 6, marginBottom: 10 }}>
        Add your response below — it will route the request back into the queue.
      </p>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{
          width: '100%',
          marginBottom: 10,
          fontFamily: 'inherit',
          fontSize: 14,
          padding: 10,
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn--primary btn--sm"
          disabled={submitting || text.trim().length === 0}
          onClick={() => onSubmit(text.trim())}
        >
          {submitting ? 'Sending…' : 'Send response'}
        </button>
      </div>
    </div>
  );
}

function AdminEditPanel({
  request,
  submitting,
  onCancel,
  onSave,
}: Readonly<{
  request: RequestRecord;
  submitting: boolean;
  onCancel: () => void;
  onSave: (patch: AdminEditRequestInput) => void;
}>) {
  const [softwareName, setSoftwareName] = useState(request.softwareName);
  const [vendor, setVendor] = useState(request.vendor ?? '');
  const [category, setCategory] = useState<Category>(request.category);
  const [logoUrl, setLogoUrl] = useState(request.logoUrl ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(request.websiteUrl);
  const [intendedUse, setIntendedUse] = useState(request.intendedUse);
  const [dataInputs, setDataInputs] = useState(request.dataInputs);
  const [expectedOutputs, setExpectedOutputs] = useState(request.expectedOutputs);
  const [outputUsage, setOutputUsage] = useState(request.outputUsage);
  const [usesConfidentialData, setConf] = useState(request.usesConfidentialData);
  const [usesPrivilegedData, setPriv] = useState(request.usesPrivilegedData);
  const [usesCustomerData, setCust] = useState(request.usesCustomerData);
  const [additionalNotes, setNotes] = useState(request.additionalNotes ?? '');
  const [reason, setReason] = useState('');

  function diff(): AdminEditRequestInput {
    const patch: AdminEditRequestInput = { reason: reason.trim() };
    if (softwareName.trim() !== request.softwareName) patch.softwareName = softwareName.trim();
    const vendorN = vendor.trim() || null;
    if (vendorN !== (request.vendor ?? null)) patch.vendor = vendorN;
    if (category !== request.category) patch.category = category;
    const logoN = logoUrl.trim() || null;
    if (logoN !== (request.logoUrl ?? null)) patch.logoUrl = logoN;
    if (websiteUrl.trim() !== request.websiteUrl) patch.websiteUrl = websiteUrl.trim();
    if (intendedUse.trim() !== request.intendedUse) patch.intendedUse = intendedUse.trim();
    if (dataInputs.trim() !== request.dataInputs) patch.dataInputs = dataInputs.trim();
    if (expectedOutputs.trim() !== request.expectedOutputs) patch.expectedOutputs = expectedOutputs.trim();
    if (outputUsage.trim() !== request.outputUsage) patch.outputUsage = outputUsage.trim();
    if (usesConfidentialData !== request.usesConfidentialData) patch.usesConfidentialData = usesConfidentialData;
    if (usesPrivilegedData !== request.usesPrivilegedData) patch.usesPrivilegedData = usesPrivilegedData;
    if (usesCustomerData !== request.usesCustomerData) patch.usesCustomerData = usesCustomerData;
    const notesN = additionalNotes.trim() || null;
    if (notesN !== (request.additionalNotes ?? null)) patch.additionalNotes = notesN;
    return patch;
  }

  const patch = diff();
  const changedKeys = Object.keys(patch).filter((k) => k !== 'reason');
  const canSave = !submitting && reason.trim().length > 0 && changedKeys.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
        Admin edit — changes are recorded on the audit trail with your reason.
      </p>

      <EditField label="Software name">
        <input value={softwareName} onChange={(e) => setSoftwareName(e.target.value)} />
      </EditField>
      <EditField label="Vendor">
        <input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="optional" />
      </EditField>
      <EditField label="Category">
        <select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </EditField>
      <EditField label="Logo URL or data:image/…">
        <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://… or paste a data URL" />
      </EditField>
      <EditField label="Website URL">
        <input type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
      </EditField>
      <EditField label="Intended use">
        <textarea rows={3} value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} />
      </EditField>
      <EditField label="Inputs">
        <textarea rows={2} value={dataInputs} onChange={(e) => setDataInputs(e.target.value)} />
      </EditField>
      <EditField label="Outputs">
        <textarea rows={2} value={expectedOutputs} onChange={(e) => setExpectedOutputs(e.target.value)} />
      </EditField>
      <EditField label="Output usage">
        <textarea rows={2} value={outputUsage} onChange={(e) => setOutputUsage(e.target.value)} />
      </EditField>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <BoolPick label="Confidential data" value={usesConfidentialData} onChange={setConf} />
        <BoolPick label="Privileged data" value={usesPrivilegedData} onChange={setPriv} />
        <BoolPick label="Customer data" value={usesCustomerData} onChange={setCust} />
      </div>
      <EditField label="Notes">
        <textarea rows={2} value={additionalNotes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" />
      </EditField>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 4 }}>
        <label className="field__label" htmlFor="admin-edit-reason">
          Reason for edit <span className="field__req">*</span>
        </label>
        <textarea
          id="admin-edit-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you changing this? Required for audit trail."
        />
        {changedKeys.length === 0 && (
          <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            No fields changed yet.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button className="btn btn--primary btn--sm" disabled={!canSave} onClick={() => onSave(patch)}>
          {submitting ? 'Saving…' : `Save ${changedKeys.length} change${changedKeys.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

function EditField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label className="field__label">{label}</label>
      {children}
    </div>
  );
}

function BoolPick({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: boolean; onChange: (v: boolean) => void }>) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--color-steel)' }}>{label}</span>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          className={`chip ${value ? 'is-active' : ''}`}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`chip ${!value ? 'is-active' : ''}`}
          onClick={() => onChange(false)}
        >
          No
        </button>
      </div>
    </div>
  );
}
